import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { UserManagement } from '@/components/settings/UserManagement'
import { Breadcrumb } from '@/components/ui/Breadcrumb'
import { useAuth } from '@/contexts/AuthContext'
import { isAdminRole } from '@/types/user'
import { Users, Key, Settings as SettingsIcon, Shield, Globe, Bell, Database, FileCode, Headphones, FileText, Mail } from 'lucide-react'

export function SettingsPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [activeSection, setActiveSection] = useState<string | null>(null)
  
  const isAdmin = isAdminRole(user?.role)
  
  const settingsCards = [
    {
      id: 'users',
      title: 'User Management',
      description: 'Manage users, roles, and permissions',
      icon: Users,
      color: 'bg-blue-500',
      available: isAdmin,
      component: UserManagement,
      route: '/settings/users'
    },
    {
      id: 'auth',
      title: 'Auth Providers',
      description: 'Configure SSO and authentication methods',
      icon: Key,
      color: 'bg-purple-500',
      available: isAdmin,
      component: null,
      route: '/settings/auth-providers'
    },
    {
      id: 'netapp',
      title: 'NetApp Config',
      description: 'Configure NetApp monitoring and data collection',
      icon: Database,
      color: 'bg-indigo-500',
      available: isAdmin,
      component: null,
      route: '/settings/netapp-config'
    },
    {
      id: 'query-templates',
      title: 'Query Templates',
      description: 'Manage NetApp GraphQL query templates',
      icon: FileCode,
      color: 'bg-cyan-500',
      available: isAdmin,
      component: null,
      route: '/settings/query-templates'
    },
    {
      id: 'helpdesk',
      title: 'Helpdesk Integration',
      description: 'Connect and manage ticketing system integrations',
      icon: Headphones,
      color: 'bg-orange-500',
      available: isAdmin,
      component: null,
      route: '/settings/helpdesk'
    },
    {
      id: 'syslog',
      title: 'Syslog',
      description: 'Configure centralized log forwarding to a syslog server',
      icon: FileText,
      color: 'bg-emerald-500',
      available: isAdmin,
      component: null,
      route: '/settings/syslog'
    },
    {
      id: 'email',
      title: 'Email',
      description: 'SMTP delivery for invitation emails',
      icon: Mail,
      color: 'bg-pink-500',
      available: isAdmin,
      component: null,
      route: '/settings/email'
    },
    {
      id: 'general',
      title: 'General Settings',
      description: 'Application preferences and configuration',
      icon: SettingsIcon,
      color: 'bg-gray-500',
      available: isAdmin,
      component: null,
      route: '/settings/general'
    },
    {
      id: 'security',
      title: 'Security',
      description: 'Password policies and security settings',
      icon: Shield,
      color: 'bg-red-500',
      available: isAdmin,
      component: null // Coming soon
    },
    {
      id: 'integrations',
      title: 'Integrations',
      description: 'Third-party services and APIs',
      icon: Globe,
      color: 'bg-green-500',
      available: isAdmin,
      component: null // Coming soon
    },
    {
      id: 'notifications',
      title: 'Notifications',
      description: 'Email and alert preferences',
      icon: Bell,
      color: 'bg-yellow-500',
      available: true,
      component: null // Coming soon
    }
  ]

  const ActiveComponent = activeSection 
    ? settingsCards.find(card => card.id === activeSection)?.component 
    : null

  if (ActiveComponent) {
    return (
      <DashboardLayout>
        <ActiveComponent onBack={() => setActiveSection(null)} />
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Breadcrumb */}
        <Breadcrumb
          items={[
            { label: 'Settings', icon: <SettingsIcon className="w-4 h-4" /> }
          ]}
        />
        
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Settings
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Configure system settings and integrations
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {settingsCards.filter(card => card.available).map((card) => {
            const Icon = card.icon
            const isDisabled = !card.component && !card.route
            
            return (
              <button
                key={card.id}
                onClick={() => {
                  if (!isDisabled) {
                    if (card.route) {
                      navigate(card.route)
                    } else if (card.component) {
                      setActiveSection(card.id)
                    }
                  }
                }}
                disabled={isDisabled}
                className={`
                  relative overflow-hidden rounded-xl border transition-all duration-300
                  ${isDisabled 
                    ? 'bg-gray-50 dark:bg-gray-900/50 border-gray-200 dark:border-gray-700 cursor-not-allowed opacity-60' 
                    : 'bg-white dark:bg-[#000742]/50 border-gray-200 dark:border-[#5869ff]/20 hover:border-[#5869ff] dark:hover:border-[#5869ff] hover:shadow-lg hover:scale-105 cursor-pointer'
                  }
                `}
              >
                <div className="p-6 text-left">
                  <div className="flex items-start justify-between mb-4">
                    <div className={`p-3 rounded-lg ${card.color} bg-opacity-10`}>
                      <Icon className={`w-6 h-6 ${card.color.replace('bg-', 'text-')}`} />
                    </div>
                    {isDisabled && (
                      <span className="px-2 py-1 text-xs font-medium text-gray-500 bg-gray-100 dark:bg-gray-800 rounded-full">
                        Coming Soon
                      </span>
                    )}
                  </div>
                  
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    {card.title}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {card.description}
                  </p>
                </div>
                
                {/* Decorative gradient */}
                {!isDisabled && (
                  <div className={`absolute top-0 right-0 w-24 h-24 ${card.color} opacity-5 rounded-full -translate-y-12 translate-x-12`} />
                )}
              </button>
            )
          })}
        </div>
      </div>
    </DashboardLayout>
  )
}